/** Lists that have to fit a cell: tags and dataset names, capped with a count. */

import { cn } from "~/lib/utils";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";

/**
 * The first `max` items plus a count of the rest, or everything when `max` is
 * unset. Shared by the tag and dataset columns, which both have to keep a table
 * row one line high whatever they hold.
 */
function capList<T>(items: T[], max: number | undefined): { shown: T[]; hidden: number } {
  if (max == null || items.length <= max) {
    return { shown: items, hidden: 0 };
  }
  return { shown: items.slice(0, max), hidden: items.length - max };
}

/** The `+2` standing for what a capped list left out. `title` holds the full set. */
const MoreCount = ({ hidden, title }: { hidden: number; title: string }) => (
  <span className="shrink-0 text-[11px] font-medium text-muted-foreground" title={title}>
    +{hidden}
  </span>
);

/**
 * A list of tags as chips, truncating rather than wrapping — the full value stays
 * on hover.
 *
 * Which fact an empty list states depends on whose tags these are: a run with no
 * selector asked every case, while a case with no tags is one no selector can
 * reach. The default reads as a case, so a run passes its own `emptyLabel`.
 */
export const TagChips = ({
  tags,
  className,
  max,
  emptyLabel = "Untagged",
}: {
  tags: string[];
  className?: string;
  /** Caps the chips at one line; the rest become a `+N`. */
  max?: number;
  /** What an empty list means here. */
  emptyLabel?: string;
}) => {
  if (tags.length === 0) {
    return (
      <span className={cn("text-xs italic text-muted-foreground", className)}>{emptyLabel}</span>
    );
  }
  const { shown, hidden } = capList(tags, max);
  return (
    <span
      className={cn(
        "flex items-center gap-1",
        max == null ? "flex-wrap" : "flex-nowrap overflow-hidden",
        className
      )}
    >
      {shown.map((tag) => (
        <span
          key={tag}
          title={tag}
          className="max-w-[140px] truncate rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground"
        >
          {tag}
        </span>
      ))}
      {hidden > 0 && <MoreCount hidden={hidden} title={tags.join(", ")} />}
    </span>
  );
};

/** What the dashboard needs of a catalog dataset: how to name it and how to colour it. */
export interface DatasetRef {
  title: string;
  /** The dataset's persisted accent, as `DatasetIcon` and the dataset cards read it. */
  accentColor: string;
}

/** Catalog datasets by id. An id the map does not hold has no catalog entry. */
export type DatasetRefs = Record<string, DatasetRef>;

/** The size of the folder beside a dataset name, in a table row and in a panel. */
const DATASET_ICON_PX = 14;

/**
 * Dataset titles under the same accented folder the rest of the app draws a
 * dataset with, off the catalog list the page already loads for the titles.
 *
 * A case can outlive its dataset, so an id with no catalog entry keeps the bare
 * id and an uncoloured folder rather than reading "unknown dataset".
 */
export const DatasetNames = ({
  ids,
  datasets,
  className,
  max,
}: {
  ids: string[];
  datasets: DatasetRefs;
  /** Sets the type size; the rows inherit it so a table can be denser than a panel. */
  className?: string;
  /** Caps the names at one line; the rest become a `+N`. */
  max?: number;
}) => {
  if (ids.length === 0) {
    return <span className={cn("text-muted-foreground", className)}>None</span>;
  }
  const { shown, hidden } = capList(ids, max);
  return (
    <span
      className={cn(
        max == null ? "flex flex-col gap-0.5" : "flex min-w-0 items-center gap-1.5",
        className
      )}
    >
      {shown.map((id) => {
        const dataset = datasets[id];
        return (
          <span
            key={id}
            className={cn(
              "flex items-center gap-1 truncate",
              dataset ? "text-foreground" : "font-mono text-[0.9em] text-muted-foreground"
            )}
            title={dataset ? `${dataset.title} (${id})` : id}
          >
            <DatasetIcon dataset={dataset} size={DATASET_ICON_PX} />
            <span className="truncate">{dataset?.title ?? id}</span>
          </span>
        );
      })}
      {hidden > 0 && (
        <MoreCount hidden={hidden} title={ids.map((id) => datasets[id]?.title ?? id).join(", ")} />
      )}
    </span>
  );
};
